import React from 'react';
import { PieChart, TrendingUp, Filter, PieChartIcon } from 'lucide-react';
import './CategoryChart.css';

function CategoryChart({ orders, timeFilter, onTimeFilterChange }) {
  const getFilteredData = (filter) => {
    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = order.createdAt.toDate();
      
      switch (filter) {
        case 'daily':
          return orderDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= weekAgo;
        case 'monthly':
          return orderDate.getMonth() === now.getMonth() && 
                 orderDate.getFullYear() === now.getFullYear();
        case 'yearly':
          return orderDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    return filteredOrders;
  };

  const getCategoryData = () => {
    const filteredOrders = getFilteredData(timeFilter);
    const categoryData = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        const category = item.category || 'Uncategorized';
        if (!categoryData[category]) {
          categoryData[category] = {
            quantity: 0,
            revenue: 0,
            products: new Set(),
            avgPrice: 0
          };
        }
        categoryData[category].quantity += item.quantity;
        categoryData[category].revenue += item.price * item.quantity;
        categoryData[category].products.add(item.name);
      });
    });

    // Calculate average price per category
    Object.keys(categoryData).forEach(category => {
      const data = categoryData[category];
      data.avgPrice = data.quantity > 0 ? data.revenue / data.quantity : 0;
      data.productCount = data.products.size;
    });

    return Object.entries(categoryData)
      .map(([category, data]) => ({
        category,
        ...data
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const categoryData = getCategoryData();
  const totalRevenue = categoryData.reduce((sum, cat) => sum + cat.revenue, 0);
  const totalQuantity = categoryData.reduce((sum, cat) => sum + cat.quantity, 0);

  // Generate colors for pie chart
  const colors = [
    '#dc2626', '#000000', '#ef4444', '#f59e0b', '#3b82f6', 
    '#8b5cf6', '#10b981', '#f97316', '#06b6d4', '#84cc16'
  ];

  // Create pie chart segments
  const createPieSegments = () => {
    let currentAngle = -90; // Start from top
    const segments = [];

    categoryData.forEach((cat, index) => {
      const percentage = (cat.revenue / totalRevenue) * 100;
      const angle = (percentage / 100) * 360;
      
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      // Calculate SVG path for pie segment
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      
      const x1 = 150 + 120 * Math.cos(startRad);
      const y1 = 150 + 120 * Math.sin(startRad);
      const x2 = 150 + 120 * Math.cos(endRad);
      const y2 = 150 + 120 * Math.sin(endRad);
      
      const largeArcFlag = angle > 180 ? 1 : 0;
      
      const path = [
        `M 150 150`,
        `L ${x1} ${y1}`,
        `A 120 120 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      segments.push({
        path,
        color: colors[index % colors.length],
        category: cat.category,
        percentage,
        revenue: cat.revenue,
        quantity: cat.quantity
      });

      currentAngle = endAngle;
    });

    return segments;
  };

  const pieSegments = createPieSegments();

  return (
    <div className="category-chart">
      <div className="chart-header">
        <h3>Category Analytics</h3>
        <div className="chart-time-filter">
          <Filter size={16} />
          <select value={timeFilter} onChange={(e) => onTimeFilterChange(e.target.value)}>
            <option value="daily">Today</option>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="yearly">This Year</option>
          </select>
        </div>
      </div>

      <div className="category-overview">
        <div className="overview-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <PieChartIcon size={24} />
            </div>
            <div className="stat-content">
              <h4>Total Categories</h4>
              <p>{categoryData.length}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon revenue">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <h4>Total Revenue</h4>
              <p>${totalRevenue.toFixed(2)}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon quantity">
              <PieChart size={24} />
            </div>
            <div className="stat-content">
              <h4>Items Sold</h4>
              <p>{totalQuantity}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="pie-chart-container">
        <div className="pie-chart-wrapper">
          <svg width="300" height="300" viewBox="0 0 300 300" className="pie-chart-svg">
            {pieSegments.map((segment, index) => (
              <g key={segment.category}>
                <path
                  d={segment.path}
                  fill={segment.color}
                  stroke="white"
                  strokeWidth="2"
                  className="pie-segment"
                  style={{
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.filter = 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))';
                  }}
                />
                
                {/* Add percentage labels for larger segments */}
                {segment.percentage > 5 && (
                  <text
                    x={150}
                    y={150}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="12"
                    fontWeight="600"
                    pointerEvents="none"
                  >
                    {segment.percentage.toFixed(1)}%
                  </text>
                )}
              </g>
            ))}
            
            {/* Center circle for donut effect */}
            <circle
              cx="150"
              cy="150"
              r="60"
              fill="white"
              stroke="none"
            />
            
            {/* Center text */}
            <text
              x="150"
              y="145"
              textAnchor="middle"
              fill="#1f2937"
              fontSize="24"
              fontWeight="700"
            >
              {categoryData.length}
            </text>
            <text
              x="150"
              y="165"
              textAnchor="middle"
              fill="#6b7280"
              fontSize="12"
              fontWeight="500"
            >
              Categories
            </text>
          </svg>
        </div>

        <div className="legend">
          {categoryData.map((cat, index) => (
            <div key={cat.category} className="legend-item">
              <div 
                className="legend-color" 
                style={{ backgroundColor: colors[index % colors.length] }}
              ></div>
              <div className="legend-info">
                <div className="legend-category">{cat.category}</div>
                <div className="legend-stats">
                  <span className="revenue">${cat.revenue.toFixed(2)}</span>
                  <span className="percentage">{((cat.revenue / totalRevenue) * 100).toFixed(1)}%</span>
                </div>
                <div className="legend-details">
                  <span>{cat.quantity} items</span>
                  <span>{cat.productCount} products</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="category-breakdown">
        <h4>Performance Metrics</h4>
        <div className="performance-grid">
          {categoryData.slice(0, 6).map((cat, index) => (
            <div key={cat.category} className="performance-card">
              <div className="performance-header">
                <div className="performance-rank">#{index + 1}</div>
                <div className="performance-icon" style={{ backgroundColor: colors[index % colors.length] }}>
                  <PieChart size={16} />
                </div>
              </div>
              <div className="performance-content">
                <h5>{cat.category}</h5>
                <div className="performance-metrics">
                  <div className="metric">
                    <span className="label">Revenue</span>
                    <span className="value">${cat.revenue.toFixed(2)}</span>
                  </div>
                  <div className="metric">
                    <span className="label">Units</span>
                    <span className="value">{cat.quantity}</span>
                  </div>
                  <div className="metric">
                    <span className="label">Avg Price</span>
                    <span className="value">${cat.avgPrice.toFixed(2)}</span>
                  </div>
                </div>
                <div className="performance-bar">
                  <div 
                    className="bar-fill" 
                    style={{ 
                      width: `${(cat.revenue / categoryData[0].revenue) * 100}%`,
                      backgroundColor: colors[index % colors.length]
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CategoryChart;
